import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gem, HelpCircle, History, Target } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useWheel500Store from '../store/useWheel500Store'
import useAppStore from '../../../store/useAppStore'
import useRealtimeGame from '../../../hooks/useRealtimeGame'
import Wheel500Wheel from '../components/Wheel500Wheel'
import Wheel500BetsFeed from '../components/Wheel500BetsFeed'
import GamePerformanceChart from '../../../components/GamePerformanceChart'
import HelpModal from '../../../components/HelpModal'
import MobileBetSheet from '../../../components/MobileBetSheet'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import TeamCapturePreview from '../../team/components/TeamCapturePreview'
import {
  WHEEL500_SECTORS,
  WHEEL500_ZONE_CONFIG,
  WHEEL500_ZONE_KEYS,
  isWheel500BettingStatus,
  isWheel500ResultStatus
} from '../config/wheel500Config'

const getStatusPollDelay = (status) => {
  if (status === 'raffle') return 300
  if (status === 'result') return 300
  if (status === 'finished') return 300
  return 1000
}

const getMyBetKey = (bet, index) => {
  const stableId = bet?.id ?? bet?.bet_id ?? bet?.created_at ?? null
  if (stableId !== null && stableId !== undefined && stableId !== '') {
    return `x50-bet-${stableId}`
  }

  const zone = String(bet?.zone || 'unknown')
  const sum = Number(bet?.sum || 0)
  const crystal = Number(bet?.crystal || 0)
  const chatId = String(bet?.chat_id || 'anon')
  return `x50-bet-fallback-${chatId}-${zone}-${sum}-${crystal}-${index}`
}

const Wheel500 = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const { playSound } = useAudioManager({
    hover: 'hover',
    betAccepted: 'doubleBetAccepted',
    win: 'doubleWin',
    select: 'select'
  })

  const {
    activeRound,
    engine,
    isConnected,
    limits,
    inventory,
    balance,
    isBetting,
    ownBetAcks,
    isModeDisabled,
    loadActiveRound,
    loadBetForm,
    loadHistoryFast,
    placeBet
  } = useWheel500Store()

  const [isBetModalOpen, setIsBetModalOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [selectedZone, setSelectedZone] = useState(null)
  const [countdownMs, setCountdownMs] = useState(Math.max(0, Number(activeRound?.second_to_raffle || 0) * 1000))
  const [recentRounds, setRecentRounds] = useState([])
  const [myBets, setMyBets] = useState([])
  const realtime = useRealtimeGame({
    game: 'wheel50', store: useWheel500Store, side: 'zone',
    onReconnect: () => refreshRecentRounds(),
    onInventory: () => { setIsBetDataReady(true); setIsStreamerRestricted(false) },
    onResult: (data) => setRecentRounds((rounds) => [
      { id: String(data.round_id), winZone: data.win_zone, winSector: String(data.win_sector) },
      ...rounds.filter((round) => round.id !== String(data.round_id))
    ].slice(0, 10)),
    onMyResult: (data) => setMyBets((betsData) => [data, ...betsData.filter((bet) => String(bet.round_id ?? bet.id) !== String(data.round_id))].slice(0, 50))
  })
  const [isBetDataReady, setIsBetDataReady] = useState(false)
  const [isStreamerRestricted, setIsStreamerRestricted] = useState(false)

  const pollTimeoutRef = useRef(null)
  const timerIntervalRef = useRef(null)
  const abortControllerRef = useRef(null)
  const isPollingActiveRef = useRef(false)
  const countdownTargetTsRef = useRef(null)
  const lastCountdownSyncRef = useRef({ roundId: null, status: null })
  const winToastRoundIdRef = useRef(null)
  const betDataRefreshPromiseRef = useRef(null)
  const betDataInitializedRef = useRef(false)
  const betDataIntervalRef = useRef(null)
  const recentHistoryPromiseRef = useRef(null)
  const recentHistoryRefreshTimeoutRef = useRef(null)
  const lastRecentHistoryRoundIdRef = useRef('initial')
  const shouldPlayBetAcceptedSoundRef = useRef(false)
  const prevMyBetKeysRef = useRef(new Set())

  const roundId = activeRound?.id
  const isRoundReady = Boolean(roundId)
  const roundStatus = activeRound?.status || null
  const bets = useMemo(() => (Array.isArray(activeRound?.bets) ? activeRound.bets : []), [activeRound?.bets])
  const roundAcks = useMemo(() => engine === 'node'
    ? ownBetAcks.filter((bet) => String(bet.round_id) === String(roundId))
    : [], [engine, ownBetAcks, roundId])
  const myZoneSums = useMemo(() => {
    const sums = { x2: 0, x3: 0, x5: 0, x50: 0 }
    const confirmed = { ...sums }
    for (const bet of bets) {
      if ((bet?.your_bet || bet?.you) && WHEEL500_ZONE_KEYS.includes(bet.zone)) sums[bet.zone] += Number(bet.sum || 0)
    }
    for (const bet of roundAcks) {
      if (WHEEL500_ZONE_KEYS.includes(bet.zone)) confirmed[bet.zone] += Number(bet.sum || 0)
    }
    return Object.fromEntries(WHEEL500_ZONE_KEYS.map((zone) => [zone, Math.max(sums[zone], confirmed[zone])]))
  }, [bets, roundAcks])

  const stats100 = useMemo(() => activeRound?.stats_100 || {}, [activeRound?.stats_100])
  const modalInventory = useMemo(() => (Array.isArray(inventory) ? inventory : []), [inventory])
  const minBet = Math.max(0, Number(limits?.min_bet || activeRound?.min_bet || 100))
  const maxBet = Math.max(0, Number(limits?.max_bet || activeRound?.max_bet || 2500000)
    - (engine === 'node' ? Object.values(myZoneSums).reduce((sum, amount) => sum + amount, 0) : 0))
  const maxBetsReached = engine === 'node' && limits?.max_bets != null && Number.isFinite(Number(limits.max_bets))
    && Math.max(bets.filter((bet) => bet?.your_bet || bet?.you).length, roundAcks.length) >= Number(limits.max_bets)
  const limitReached = engine === 'node' && maxBet < minBet
  const isBetLockedByStatus = !isRoundReady || maxBetsReached || limitReached || ['raffle', 'result', 'finished'].includes(roundStatus)
    || (engine === 'node' && (!isConnected || !activeRound?.ends_at || Date.now() + realtime.clockOffset >= activeRound.ends_at))
  const isBetLocked = isBetLockedByStatus || isStreamerRestricted
  const isResultPhase = isWheel500ResultStatus(roundStatus)
  const winZone = activeRound?.win_zone || null
  const currentBalance = Number(balance || 0)

  const betDisabledReason = isStreamerRestricted
    ? t('wheel500.bet.streamerRestricted', 'Для вашего аккаунта ставки в X50 недоступны')
    : maxBetsReached
      ? t('realtime.alerts.too_many_bets')
      : limitReached
        ? t('realtime.alerts.error_max_bet')
        : isBetLockedByStatus
          ? t('wheel500.bet.locked', 'Ставки не принимаются')
          : ''

  const handleStopPolling = () => {
    isPollingActiveRef.current = false

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const startPolling = useCallback(async () => {
    if (!isPollingActiveRef.current) return

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const round = await loadActiveRound(controller.signal)
      if (!isPollingActiveRef.current) return

      if (!round) {
        pollTimeoutRef.current = setTimeout(startPolling, isGuest ? 10000 : 2000)
        return
      }

      const nextDelay = isGuest ? 10000 : getStatusPollDelay(round?.status)
      pollTimeoutRef.current = setTimeout(startPolling, nextDelay)
    } catch (error) {
      const isAbortError = error?.name === 'AbortError' || controller.signal.aborted
      if (!isPollingActiveRef.current || isAbortError) return
      pollTimeoutRef.current = setTimeout(startPolling, isGuest ? 10000 : 1500)
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
      }
    }
  }, [isGuest, loadActiveRound])

  const refreshBetData = useCallback(async ({ showError = false } = {}) => {
    if (isGuest) return false
    if (betDataRefreshPromiseRef.current) return betDataRefreshPromiseRef.current

    betDataRefreshPromiseRef.current = (async () => {
      try {
        await loadBetForm()
        setIsBetDataReady(true)
        setIsStreamerRestricted(false)
        return true
      } catch (error) {
        if (error?.code === 'streamer_restricted') {
          setIsStreamerRestricted(true)
          return false
        }

        if (showError) {
          hapticNotify('error')
          toast.error(t('wheel500.toasts.betFormError', 'Не удалось загрузить форму ставки'))
        }
        return false
      } finally {
        betDataRefreshPromiseRef.current = null
      }
    })()

    return betDataRefreshPromiseRef.current
  }, [isGuest, loadBetForm, t])

  const refreshRecentRounds = useCallback(async () => {
    if (recentHistoryPromiseRef.current) return recentHistoryPromiseRef.current

    recentHistoryPromiseRef.current = (async () => {
      try {
        const { rounds, myBets: betsData } = await loadHistoryFast()

        setMyBets((current) => useWheel500Store.getState().engine === 'node'
          ? [...current, ...betsData.filter((bet) => !current.some((item) => String(item.round_id ?? item.id) === String(bet.round_id ?? bet.id)))].slice(0, 50)
          : betsData)

        const normalizedRounds = rounds
          .filter((round) => round?.win_sector !== null && typeof round?.win_sector !== 'undefined')
          .slice(0, 10)
          .map((round) => ({
            id: String(round?.id || ''),
            winZone: String(round?.win_zone || '').toLowerCase(),
            winSector: String(round?.win_sector ?? '—')
          }))

        setRecentRounds((current) => useWheel500Store.getState().engine === 'node'
          ? [...current, ...normalizedRounds.filter((round) => !current.some((item) => item.id === round.id))].slice(0, 10)
          : normalizedRounds)
      } catch (error) {
        // Ошибку истории не показываем тостом, чтобы не отвлекать от игры
      } finally {
        recentHistoryPromiseRef.current = null
      }
    })()

    return recentHistoryPromiseRef.current
  }, [loadHistoryFast])

  useEffect(() => {
    if (engine !== 'worker') return undefined
    isPollingActiveRef.current = true
    startPolling()
    return () => handleStopPolling()
  }, [engine, startPolling])

  useEffect(() => {
    const nextRoundId = activeRound?.id || null
    const nextStatus = activeRound?.status || null
    const nextMilliseconds = Math.max(0, Number(activeRound?.second_to_raffle || 0) * 1000)
    const prevSync = lastCountdownSyncRef.current

    const isNewRound = prevSync.roundId !== nextRoundId
    const enteredBetStatus = !isWheel500BettingStatus(prevSync.status) && isWheel500BettingStatus(nextStatus)

    if ((isNewRound || enteredBetStatus || (engine === 'node' && activeRound?.ends_at)) && isWheel500BettingStatus(nextStatus)) {
      countdownTargetTsRef.current = engine === 'node' && activeRound?.ends_at
        ? activeRound.ends_at - realtime.clockOffset
        : Date.now() + nextMilliseconds
      setCountdownMs(Math.max(0, countdownTargetTsRef.current - Date.now()))
    }

    if (!isWheel500BettingStatus(nextStatus)) {
      countdownTargetTsRef.current = null
    }

    lastCountdownSyncRef.current = {
      roundId: nextRoundId,
      status: nextStatus
    }
  }, [activeRound?.id, activeRound?.status, activeRound?.second_to_raffle, activeRound?.ends_at, engine, realtime.clockOffset])

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    if (!isWheel500BettingStatus(roundStatus)) return

    timerIntervalRef.current = setInterval(() => {
      if (!countdownTargetTsRef.current) return

      const remainingMs = Math.max(0, countdownTargetTsRef.current - Date.now())
      setCountdownMs(remainingMs)

      if (remainingMs <= 0 && timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }, 33)

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [roundStatus])

  useEffect(() => {
    if (!roundId || !isResultPhase || (engine === 'node' && !Array.isArray(activeRound?.winners))) return
    if (winToastRoundIdRef.current === roundId) return

    const hasMyWinningBet = bets.some((bet) => (bet?.your_bet || bet?.you) && bet?.win === true) || myZoneSums[winZone] > 0
    if (!hasMyWinningBet) return

    winToastRoundIdRef.current = roundId
    playSound('win')
    hapticImpact('heavy')
    hapticNotify('success')
    toast.success(t('wheel500.toasts.youWon', 'Вы выиграли!'))
  }, [activeRound?.winners, bets, engine, isResultPhase, myZoneSums, playSound, roundId, t, winZone])

  useEffect(() => {
    const myBetKeys = new Set(
      bets
        .map((bet, index) => ({ bet, index }))
        .filter(({ bet }) => Boolean(bet?.your_bet || bet?.you))
        .map(({ bet, index }) => getMyBetKey(bet, index))
    )

    const prevMyBetKeys = prevMyBetKeysRef.current
    const hasNewMyBet = Array.from(myBetKeys).some((key) => !prevMyBetKeys.has(key))

    if (shouldPlayBetAcceptedSoundRef.current && hasNewMyBet) {
      playSound('betAccepted')
      shouldPlayBetAcceptedSoundRef.current = false
    }

    prevMyBetKeysRef.current = myBetKeys
  }, [bets, playSound])

  useEffect(() => {
    if (!engine || isGuest) return undefined
    if (!betDataInitializedRef.current) {
      betDataInitializedRef.current = true
      refreshBetData()
    }
    if (engine === 'node') return undefined

    if (betDataIntervalRef.current) {
      clearInterval(betDataIntervalRef.current)
      betDataIntervalRef.current = null
    }

    betDataIntervalRef.current = setInterval(() => {
      refreshBetData()
    }, 5000)

    return () => {
      if (betDataIntervalRef.current) {
        clearInterval(betDataIntervalRef.current)
        betDataIntervalRef.current = null
      }
    }
  }, [engine, isGuest, refreshBetData])

  useEffect(() => {
    if (recentHistoryRefreshTimeoutRef.current) {
      clearTimeout(recentHistoryRefreshTimeoutRef.current)
      recentHistoryRefreshTimeoutRef.current = null
    }

    const nextRoundId = roundId || null
    const prevRoundId = lastRecentHistoryRoundIdRef.current
    const isFirstHistorySync = prevRoundId === 'initial'
    const isNewRound = !isFirstHistorySync && prevRoundId !== nextRoundId

    lastRecentHistoryRoundIdRef.current = nextRoundId

    if (isNewRound && engine === 'node') return undefined

    if (isNewRound) {
      recentHistoryRefreshTimeoutRef.current = setTimeout(() => {
        refreshRecentRounds()
        recentHistoryRefreshTimeoutRef.current = null
      }, 2000)

      return () => {
        if (recentHistoryRefreshTimeoutRef.current) {
          clearTimeout(recentHistoryRefreshTimeoutRef.current)
          recentHistoryRefreshTimeoutRef.current = null
        }
      }
    }

    if (isFirstHistorySync) refreshRecentRounds()

    return () => {
      if (recentHistoryRefreshTimeoutRef.current) {
        clearTimeout(recentHistoryRefreshTimeoutRef.current)
        recentHistoryRefreshTimeoutRef.current = null
      }
    }
  }, [engine, refreshRecentRounds, roundId])

  const handleOpenBetModal = async (zone) => {
    if (isGuest) return openLogin()
    if (isBetLocked) return

    playSound('select')
    hapticImpact('medium')

    const prepared = isBetDataReady ? true : await refreshBetData({ showError: true })
    if (!prepared) return

    const currentState = useWheel500Store.getState()
    if (currentState.isModeDisabled || currentState.activeRound?.id !== roundId || ['raffle', 'result', 'finished'].includes(currentState.activeRound?.status)) return

    setSelectedZone(zone)
    setIsBetModalOpen(true)
  }

  const handleSubmitBet = async ({ zone, crystal, items }) => {
    if (!roundId) {
      hapticNotify('error')
      toast.error(t('wheel500.toasts.roundUnavailable', 'Раунд недоступен'))
      return false
    }

    try {
      if (engine === 'node') {
        await realtime.placeBet({ roundId, zone, crystal, items })
        playSound('betAccepted')
      } else {
        await placeBet({ roundId, zone, crystal, items })
        shouldPlayBetAcceptedSoundRef.current = true
        await Promise.allSettled([loadActiveRound(), refreshBetData()])
      }

      hapticNotify('success')
      setIsBetModalOpen(false)
      return true
    } catch (error) {
      if (error?.status === 409 || error?.message === 'engine_switched') {
        await loadActiveRound().catch(() => {})
        if (useWheel500Store.getState().engine === 'node') realtime.requestState()
        setIsBetModalOpen(false)
        return false
      }
      if (engine === 'node') {
        hapticNotify('error')
        toast.error(t(`realtime.alerts.${error.message}`, t('wheel500.toasts.betError', 'Не удалось отправить ставку')))
        return false
      }
      if (error?.code === 'round_not_found') {
        await Promise.allSettled([
          loadActiveRound(),
          refreshBetData()
        ])
        setIsBetModalOpen(false)
        return false
      }

      hapticNotify('error')

      if (error?.code === 'streamer_restricted') {
        setIsStreamerRestricted(true)
        toast.error(t('wheel500.toasts.streamerRestricted', 'Для вашего аккаунта ставки недоступны'))
        return false
      }

      if (error?.message === 'not enough balance') {
        toast.error(t('wheel500.toasts.notEnoughBalance', 'Недостаточно кристаллов'))
        return false
      }
      if (error?.message === 'error sum') {
        toast.error(t('wheel500.toasts.minBet', 'Минимальная ставка {{amount}}', { amount: minBet.toLocaleString() }))
        return false
      }
      if (error?.message === 'error max bet') {
        toast.error(t('wheel500.toasts.maxBet', 'Превышен лимит ставки'))
        return false
      }
      if (error?.message === 'too many bets') {
        toast.error(t('wheel500.toasts.tooManyBets', 'Слишком много ставок в раунде'))
        return false
      }

      toast.error(t('wheel500.toasts.betError', 'Не удалось отправить ставку'))
      return false
    }
  }

  const statsRows = useMemo(() => {
    const total = WHEEL500_ZONE_KEYS.reduce((acc, zoneKey) => acc + Number(stats100?.[zoneKey] || 0), 0)
    const safeTotal = total > 0 ? total : 1

    return WHEEL500_ZONE_KEYS.map((zoneKey) => {
      const value = Number(stats100?.[zoneKey] || 0)
      const zoneCfg = WHEEL500_ZONE_CONFIG[zoneKey]

      return {
        key: zoneKey,
        label: zoneCfg.label,
        value,
        hex: zoneCfg.hex,
        percent: (value / safeTotal) * 100
      }
    })
  }, [stats100])

  if (isModeDisabled) {
    return (
      <div className="px-4 py-6 pb-16 lg:px-6 lg:py-4 lg:pb-8">
        <div className="mx-auto max-w-4xl rounded-2xl border border-white/10 bg-surface/50 p-6 text-center ">
          <h1 className="text-2xl font-bold text-white ">{t('wheel500.mode.title', 'Режим X50')}</h1>
          <p className="mt-3 text-text-secondary">{t('realtime.disabled')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8 overflow-x-clip">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2">
            <h1 className="flex items-center gap-2 text-2xl lg:text-4xl font-bold text-accent-primary uppercase italic">
              <Target className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
              <span>{t('wheel500.title', 'X50')}</span>
            </h1>
            <button
              type="button"
              onClick={() => setIsHelpOpen(true)}
              className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
              aria-label={t('wheel500.help.button', 'Справка по режиму X50')}
            >
              <HelpCircle className="w-5 h-5 text-text-secondary" />
            </button>
          </div>
        </div>
        {engine === 'node' && !isConnected && realtime.showReconnectNotice && (
          <div className="flex justify-center">
            <span role="status" className="rounded-full border border-white/10 bg-surface px-3 py-1 text-xs text-text-secondary">
              {t('realtime.reconnecting')}
            </span>
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_350px]">
          <div className="min-w-0 space-y-6">
            <Wheel500Wheel
              sectors={WHEEL500_SECTORS}
              status={roundStatus}
              winSectorIndex={activeRound?.win_sector == null ? undefined : Number(activeRound.win_sector)}
              prevSectorIndex={Number.isInteger(activeRound?.prev_round?.win_sector) ? activeRound.prev_round.win_sector : undefined}
              roundId={roundId}
              raffleSeconds={activeRound?.second_to_finish_raffle}
              raffleDurationSeconds={activeRound?.raffle_time}
              raffleEndTimeMs={engine === 'node' && activeRound?.anim_ends_at
                ? Number(activeRound.anim_ends_at) - realtime.clockOffset
                : activeRound?.received_at && activeRound?.second_to_finish_raffle != null
                  ? activeRound.received_at + Number(activeRound.second_to_finish_raffle) * 1000
                  : null}
              countdownMs={countdownMs}
              winZone={winZone}
            />

            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
              {WHEEL500_ZONE_KEYS.map((zoneKey) => {
                const zoneCfg = WHEEL500_ZONE_CONFIG[zoneKey]
                const isWinner = isResultPhase && winZone === zoneKey
                const mySum = myZoneSums[zoneKey] || 0

                return (
                  <button
                    key={zoneKey}
                    type="button"
                    onClick={() => handleOpenBetModal(zoneKey)}
                    onMouseEnter={() => playSound('hover')}
                    disabled={isBetLocked}
                    style={{
                      borderColor: isWinner ? `${zoneCfg.hex}cc` : `${zoneCfg.hex}66`,
                      background: isWinner
                        ? `linear-gradient(135deg, ${zoneCfg.hex}d4 0%, ${zoneCfg.hex}8f 55%, rgba(7,10,18,0.88) 100%)`
                        : `linear-gradient(135deg, ${zoneCfg.hex}8a 0%, ${zoneCfg.hex}57 55%, rgba(7,10,18,0.82) 100%)`,
                      boxShadow: isWinner
                        ? `0 0 30px ${zoneCfg.hex}80, 0 0 0 2px ${zoneCfg.hex}99`
                        : `0 8px 24px ${zoneCfg.hex}33`
                    }}
                    className={`group relative min-h-[72px] overflow-hidden rounded-2xl border px-3 py-3 text-left transition-all duration-300 ${
                      isWinner ? 'animate-pulse' : ''
                    } ${
                      isBetLocked && !(isResultPhase && winZone === zoneKey) ? 'cursor-not-allowed opacity-50 transform-none !shadow-none' : 'hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0'
                    }`}
                  >
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.22),transparent_45%)]" />

                    <span
                      className="absolute inset-y-2 left-0 w-1 rounded-full"
                      style={{ backgroundColor: zoneCfg.hex, boxShadow: `0 0 10px ${zoneCfg.hex}aa` }}
                    />

                    <div className="relative flex flex-wrap items-center justify-between gap-x-2 gap-y-2 pl-2">
                      <span className="text-2xl font-black not-italic leading-none tracking-tight text-white sm:text-3xl">
                        ×{zoneCfg.multiplier}
                      </span>
                      {mySum > 0 && (
                        <span className="inline-flex items-center gap-1 text-sm font-medium tabular-nums text-white/90">
                          <span className="sr-only">{t('wheel500.round.myBet', 'Ваша ставка')}: </span>
                          {mySum.toLocaleString()} <Gem className="h-3.5 w-3.5 shrink-0" />
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>

            <Wheel500BetsFeed bets={bets} status={roundStatus} winColor={winZone} disableAnimation={engine === 'node' && activeRound?.winners === null} />

            {!isGuest && myBets.length > 0 && <div className="lg:hidden">
              <GamePerformanceChart myBets={myBets} mode="wheel500" gameName="X50" />
            </div>}
          </div>

          <div className="space-y-6 h-fit">
            <div className="hidden lg:block">
              {isGuest ? <button type="button" onClick={openLogin} className="gradient-border-cta w-full rounded-xl p-4 font-bold text-dark-primary">{t('guest.login')}</button> : <MobileBetSheet
                variant="inline"
                mode="wheel500"
                targets={WHEEL500_ZONE_KEYS.map((zone) => ({
                  value: zone,
                  label: WHEEL500_ZONE_CONFIG[zone].label,
                  style: {
                    borderColor: `${WHEEL500_ZONE_CONFIG[zone].hex}66`,
                    backgroundColor: `${WHEEL500_ZONE_CONFIG[zone].hex}1f`,
                    color: WHEEL500_ZONE_CONFIG[zone].hex
                  }
                }))}
                isDisabled={isBetLocked}
                disabledReason={betDisabledReason}
                isLoading={isBetting}
                isDataReady={isBetDataReady}
                balance={currentBalance}
                inventory={modalInventory}
                minBet={minBet}
                maxBet={maxBet}
                maxItems={engine === 'node' ? Number(limits?.max_items ?? Infinity) : Infinity}
                onSubmit={handleSubmitBet}
              />}
            </div>

            {!isGuest && myBets.length > 0 && <div className="hidden lg:block">
              <GamePerformanceChart myBets={myBets} mode="wheel500" gameName="X50" />
            </div>}

            <div className="rounded-2xl border border-white/10 bg-surface/50 p-4 space-y-3">
              <h3 className="text-white font-semibold">{t('wheel500.stats.title', 'Статистика 100 раундов')}</h3>

              <div className="space-y-2">
                {statsRows.map((row) => (
                  <div key={row.key} className="flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1.5 text-text-secondary min-w-0">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0 ring-1 ring-white/15" style={{ backgroundColor: row.hex }} />
                      <span className="truncate">{row.label}</span>
                    </span>
                    <span className="text-white font-medium tabular-nums">{row.percent.toFixed(0)}%</span>
                  </div>
                ))}
              </div>

              {recentRounds.length > 0 && (
                <div className="border-t border-white/10 pt-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {recentRounds.slice(0, 8).map((round) => {
                      const zoneCfg = WHEEL500_ZONE_CONFIG[round.winZone]

                      return (
                        <button
                          type="button"
                          key={round.id || `${round.winZone}-${round.winSector}`}
                          onClick={() => round.id && navigate(`/x50/history/${round.id}`)}
                          disabled={!round.id}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold tabular-nums disabled:cursor-default"
                          style={{
                            color: zoneCfg?.hex || '#fff',
                            borderColor: `${zoneCfg?.hex || '#fff'}77`,
                            backgroundColor: `${zoneCfg?.hex || '#fff'}22`
                          }}
                          title={`${round.winZone} #${round.winSector}`}
                        >
                          {round.winSector}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate('/x50/history')}
              className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors"
            >
              <History className="w-4 h-4" />
              <span className="text-sm">{t('wheel500.buttons.history', 'История')}</span>
            </button>
          </div>
        </div>
        <TeamCapturePreview mode="wheel50" className="mx-auto max-w-3xl" />
      </div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('wheel500.help.title', 'Как работает X50')}
        steps={[
          t('wheel500.help.step1', 'Выберите зону ставки: x2, x3, x5 или x50. Чем выше множитель, тем меньше секторов этой зоны на колесе.'),
          t('wheel500.help.step2', 'Добавьте кристаллы и/или предметы, минимальная ставка — 100, а максимальная — 2 500 000.'),
          t('wheel500.help.step3', 'Дождитесь розыгрыша: колесо из 54 секторов определит выигравшую зону раунда.'),
          t('wheel500.help.step4', 'Если колесо остановится на вашей зоне, ваша ставка умножится на множитель зоны.')
        ]}
        highlightLastStep={true}
      />

      <MobileBetSheet
        isOpen={isBetModalOpen}
        mode="wheel500"
        target={selectedZone}
        targetLabel={selectedZone ? WHEEL500_ZONE_CONFIG[selectedZone]?.label : ''}
        minBet={minBet}
        maxBet={maxBet}
        maxItems={engine === 'node' ? Number(limits?.max_items ?? Infinity) : Infinity}
        isDisabled={isBetLocked}
        disabledReason={betDisabledReason}
        isLoading={isBetting}
        balance={currentBalance}
        inventory={modalInventory}
        onClose={() => setIsBetModalOpen(false)}
        onSubmit={handleSubmitBet}
      />
    </div>
  )
}

export default Wheel500
